import React, { useState, useRef } from 'react';
import Tooltip from '../common/Tooltip';

const LayoutVariationsDemo: React.FC = () => {
  const [demoMode, setDemoMode] = useState('all-elements');
  const [showInnerSplit, setShowInnerSplit] = useState(true);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const infoAnchorRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div>
      <div className="css-info">
        <div>Layout Variations Demonstration</div>
        <button
          ref={infoAnchorRef}
          className="css-info-icon"
          aria-label="CSS info"
          title="CSS info"
          aria-expanded={tooltipVisible}
          aria-controls="css-tooltip-layout"
          onClick={() => setTooltipVisible(v => !v)}
        >
          <span className="sr-only">CSS info</span>
        </button>
        <Tooltip anchorRef={infoAnchorRef} isOpen={tooltipVisible} onClose={() => setTooltipVisible(false)} id="css-tooltip-layout">
          CSS Classes Used:
          ALL LAYOUT HIERARCHY CLASSES - Complete demonstration of:
          Level 1: .full-area system
          Level 2: .body-grid (.menu-area + .page-area)
          Level 3: .page-area (.module-row + .module-area)
          Level 4: .module-area (.module-content-area + .module-widget-area)
          Level 5: .tabs-row + .tab-content-area
          Level 6: .left-menu-area + .mid-content-area (inner split)
          
          This demo shows how optional elements can be toggled.
    </Tooltip>
      </div>

      {/* Controls */}
      <div className="container-toolbar">
        <div className="layout-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="layout-flex-row" style={{ gap: '16px', alignItems: 'center' }}>
            <span className="text-heading-small">Layout Mode:</span>
            <select 
              value={demoMode}
              onChange={(e) => setDemoMode(e.target.value)}
              className="control-form-select"
            >
              <option value="all-elements">All Elements (Full 6-Level)</option>
              <option value="no-widgets">No Right Widgets</option>
              <option value="no-tabs">No Tab System</option>
              <option value="minimal">Minimal Layout</option>
            </select>
          </div>
          
          <label className="layout-flex-row" style={{ gap: '8px', alignItems: 'center' }}>
            <input 
              type="checkbox" 
              checked={showInnerSplit}
              onChange={(e) => setShowInnerSplit(e.target.checked)}
            />
            <span className="text-label-primary">Show Inner Split (Level 6)</span>
          </label>
        </div>
      </div>

      {/* Layout Preview */}
      <div className="visual-card-large" style={{ height: '400px', overflow: 'hidden' }}>
        <div className="visual-card-header">
          <h4 className="text-heading-small">
            Current Layout: {demoMode.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </h4>
        </div>

        {/* Miniature Layout Representation */}
        <div style={{ 
          height: '350px', 
          border: '2px solid #4a90e2', 
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '10px',
          fontWeight: '600'
        }}>
          
          {/* Level 1: Header */}
          <div style={{ 
            height: '24px', 
            background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '8px',
            borderBottom: '1px solid #ccc'
          }}>
            Level 1: Header Row (.header-row)
          </div>

          {/* Level 2: Body Grid */}
          <div style={{ flex: 1, display: 'flex', borderBottom: '1px solid #ccc' }}>
            
            {/* Menu Area */}
            <div style={{ 
              width: '40px', 
              background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              writingMode: 'vertical-lr',
              borderRight: '1px solid #ccc'
            }}>
              Menu
            </div>

            {/* Level 3: Page Area */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              
              {/* Module Row */}
              <div style={{ 
                height: '20px', 
                background: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '8px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                Level 3: Module Row (.module-row)
              </div>

              {/* Level 4: Module Area */}
              <div style={{ flex: 1, display: 'flex' }}>
                
                {/* Module Content Area */}
                <div style={{ 
                  flex: demoMode === 'no-widgets' ? 1 : 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRight: demoMode === 'no-widgets' ? 'none' : '1px solid #e5e7eb'
                }}>
                  
                  {/* Level 5: Tabs (if enabled) */}
                  {demoMode !== 'no-tabs' && demoMode !== 'minimal' && (
                    <div style={{ 
                      height: '18px', 
                      background: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '8px',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      Level 5: Tabs Row (.tabs-row)
                    </div>
                  )}

                  {/* Level 6: Tab Content / Inner Split */}
                  <div style={{ flex: 1, display: 'flex' }}>
                    
                    {showInnerSplit && demoMode !== 'minimal' && (
                      <div style={{ 
                        width: '60px', 
                        background: '#f9fafb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        writingMode: 'vertical-lr',
                        borderRight: '1px solid #e5e7eb',
                        fontSize: '9px'
                      }}>
                        L6: Left Menu
                      </div>
                    )}

                    <div style={{ 
                      flex: 1, 
                      background: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      <div>Level 6: Mid Content Area</div>
                      <div style={{ fontSize: '8px', color: '#6b7280' }}>
                        (.mid-content-area)
                      </div>
                      <div style={{ fontSize: '8px', color: '#6b7280' }}>
                        Main working content goes here
                      </div>
                    </div>
                  </div>
                </div>

                {/* Module Widget Area (if enabled) */}
                {demoMode !== 'no-widgets' && demoMode !== 'minimal' && (
                  <div style={{ 
                    width: '80px', 
                    background: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '4px',
                    fontSize: '9px'
                  }}>
                    <div>Level 4:</div>
                    <div>Widget Area</div>
                    <div style={{ fontSize: '8px', color: '#6b7280' }}>
                      (.module-widget-area)
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Level 1: Footer */}
          <div style={{ 
            height: '16px', 
            background: '#374151',
            color: '#d1d5db',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '8px'
          }}>
            Level 1: Footer Row (.footer-row)
          </div>
        </div>
      </div>

      {/* CSS Class Breakdown */}
      <div style={{ marginTop: '24px' }}>
        <h4 className="text-heading-medium" style={{ marginBottom: '16px' }}>
          Active CSS Classes in Current Layout
        </h4>
        
        <div className="layout-grid-2col">
          {/* Mandatory Classes */}
          <div className="visual-card-medium">
            <div className="visual-card-header">
              <h5 className="text-heading-small">✅ Mandatory Classes (Always Present)</h5>
            </div>
            <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6' }}>
              <div>• .full-area</div>
              <div>• .header-row</div>
              <div>• .body-grid</div>
              <div>• .menu-area</div>
              <div>• .page-area</div>
              <div>• .module-row</div>
              <div>• .module-area</div>
              <div>• .module-content-area</div>
              <div>• .tab-content-area</div>
              <div>• .mid-content-area</div>
              <div>• .footer-row</div>
            </div>
          </div>

          {/* Optional Classes */}
          <div className="visual-card-medium">
            <div className="visual-card-header">
              <h5 className="text-heading-small">⚡ Optional Classes (Conditional)</h5>
            </div>
            <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6' }}>
              <div style={{ opacity: demoMode === 'no-widgets' || demoMode === 'minimal' ? 0.4 : 1 }}>
                • .module-widget-area {demoMode === 'no-widgets' || demoMode === 'minimal' ? ' (hidden)' : ' ✅'}
              </div>
              <div style={{ opacity: demoMode === 'no-tabs' || demoMode === 'minimal' ? 0.4 : 1 }}>
                • .tabs-row {demoMode === 'no-tabs' || demoMode === 'minimal' ? ' (hidden)' : ' ✅'}
              </div>
              <div style={{ opacity: !showInnerSplit || demoMode === 'minimal' ? 0.4 : 1 }}>
                • .left-menu-area {!showInnerSplit || demoMode === 'minimal' ? ' (hidden)' : ' ✅'}
              </div>
            </div>
            
            <div style={{ marginTop: '12px', fontSize: '11px', color: '#6b7280' }}>
              Optional elements enhance functionality but aren't always needed. 
              The layout system gracefully handles their presence or absence.
            </div>
          </div>
        </div>
      </div>

      {/* Height Calculation for Current Mode */}
      <div style={{ marginTop: '20px' }}>
        <div className="visual-card-medium">
          <div className="visual-card-header">
            <h5 className="text-heading-small">📐 Height Calculation for Current Mode</h5>
          </div>
          <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.8' }}>
            <div><strong>Base:</strong> 100vh</div>
            <div><strong>- Header:</strong> 100vh - 70px</div>
            <div><strong>- Footer:</strong> 100vh - 70px - 32px</div>
            <div><strong>- Module Row:</strong> 100vh - 70px - 32px - 44px</div>
            <div><strong>- Padding:</strong> 100vh - 70px - 32px - 44px - 32px</div>
            {demoMode !== 'no-tabs' && demoMode !== 'minimal' && (
              <div><strong>- Tab Row:</strong> 100vh - 70px - 32px - 44px - 32px - 44px</div>
            )}
            {showInnerSplit && demoMode !== 'minimal' && (
              <div><strong>- Inner Gap:</strong> 100vh - 70px - 32px - 44px - 32px - 44px - 12px</div>
            )}
            <div style={{ marginTop: '8px', color: '#4a90e2', fontWeight: '600' }}>
              <strong>Final Content Height:</strong> {
                demoMode === 'minimal' ? '100vh - 178px' :
                demoMode === 'no-tabs' ? '100vh - 178px' :
                showInnerSplit ? '100vh - 234px' : '100vh - 222px'
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutVariationsDemo;